import React, { useState } from "react"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { isValidPhoneNumber } from "react-phone-number-input"
import { PhoneInput } from "@/components/ui/phone-input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import {Api} from '@/lib/api.ts';

const hearAboutOptions = [
  "Referral",
  "Online Search",
  "GrandPad Website",
  "LinkedIn",
  "Other Social Media (e.g., Facebook, Instagram)",
  "Amazon",
  "News or Press Coverage",
  "Conference or Industry Event",
  "Email or Newsletter",
  "Other (please specify)",
] as const;

const formSchema = z.object({
  firstName: z.string().trim().min(2, "First name must be at least 2 characters").max(100),
  lastName: z.string().trim().min(2, "Last name must be at least 2 characters").max(100),
  email: z.string().trim().email("Please enter a valid email address").max(255),
  phone: z.string().refine((v) => !!v && isValidPhoneNumber(v), "Please enter a valid phone number"),
  seniorsServed: z.string().min(1, "Please let us know how many seniors you serve"),
  grandpadsInterested: z.string().min(1, "Please let us know how many GrandPads you're interested in"),
  how_did_you_hear: z.string().min(1, "Please select how you heard about GrandPad"),
  how_did_you_hear_other: z.string().trim().max(250).optional(),
  additional_message: z.string().trim().max(2000).optional(),
}).superRefine((value, context) => {
  if (value.how_did_you_hear === "Other (please specify)" && !value.how_did_you_hear_other) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["how_did_you_hear_other"], message: "Please specify how you heard about GrandPad" })
  }
})

type FormData = z.infer<typeof formSchema>

interface BookDemoFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function BookDemoForm({ open, onOpenChange }: BookDemoFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
    control,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { phone: "" },
  })

  const howDidYouHear = watch("how_did_you_hear")
  const [isSuccess, setIsSuccess] = useState(false)
  const [submitError, setSubmitError] = useState("")

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset()
      setIsSuccess(false)
      setSubmitError("")
    }
    onOpenChange(nextOpen)
  }

  const onSubmit = async (data: FormData) => {
    setSubmitError("")
    const ok = await Api.Post('/api/submit_demo_request', data)
    if (!ok) {
      setSubmitError("We couldn't submit your request right now. Please try again.")
      return
    }
    reset()
    setIsSuccess(true)
  }


  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        {isSuccess ? (
          <div className="py-8 text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-beacon/10 flex items-center justify-center">
              <CheckCircleIcon className="w-8 h-8 text-beacon" />
            </div>
            <div className="space-y-2" aria-live="polite">
              <DialogTitle>Thank you!</DialogTitle>
              <DialogDescription className="text-body text-muted-foreground">
                Your demo request has been submitted. We'll be in touch soon to schedule your personalized demo.
              </DialogDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSubmitError("")
                setIsSuccess(false)
              }}
              className="rounded-full"
            >
              Submit another request
            </Button>
          </div>
        ) : (
          <>
        <DialogHeader>
          <DialogTitle>Book a Demo</DialogTitle>
          <DialogDescription>
            Fill out the form below and we'll get in touch to schedule your personalized demo.
          </DialogDescription>
        </DialogHeader>
        <form id="gppk-b2b-book-demo-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                {...register("firstName")}
                placeholder="Enter your first name"
                aria-invalid={errors.firstName ? "true" : "false"}
                aria-describedby={errors.firstName ? "firstName-error" : undefined}
              />
              {errors.firstName && (
                <p className="text-body-sm text-destructive" role="alert" id="firstName-error">
                  {errors.firstName.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                {...register("lastName")}
                placeholder="Enter your last name"
                aria-invalid={errors.lastName ? "true" : "false"}
                aria-describedby={errors.lastName ? "lastName-error" : undefined}
              />
              {errors.lastName && (
                <p className="text-body-sm text-destructive" role="alert" id="lastName-error">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              placeholder="Enter your email address"
              aria-invalid={errors.email ? "true" : "false"}
              aria-describedby={errors.email ? "email-error" : undefined}
            />
            {errors.email && (
              <p className="text-body-sm text-destructive" role="alert" id="email-error">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  id="phone"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="Enter your phone number"
                  aria-invalid={errors.phone ? "true" : "false"}
                  aria-describedby={errors.phone ? "phone-error" : undefined}
                />
              )}
            />
            {errors.phone && (
              <p className="text-body-sm text-destructive" role="alert" id="phone-error">
                {errors.phone.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="seniorsServed">How many seniors do you serve? *</Label>
            <Input
              id="seniorsServed"
              {...register("seniorsServed")}
              placeholder="e.g., 100, 500, 1000+"
              aria-invalid={errors.seniorsServed ? "true" : "false"}
              aria-describedby={errors.seniorsServed ? "seniorsServed-error" : undefined}
            />
            {errors.seniorsServed && (
              <p className="text-body-sm text-destructive" role="alert" id="seniorsServed-error">
                {errors.seniorsServed.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="grandpadsInterested">How many GrandPads are you interested in? *</Label>
            <Input
              id="grandpadsInterested"
              {...register("grandpadsInterested")}
              placeholder="e.g., 10, 50, 100+"
              aria-invalid={errors.grandpadsInterested ? "true" : "false"}
              aria-describedby={errors.grandpadsInterested ? "grandpadsInterested-error" : undefined}
            />
            {errors.grandpadsInterested && (
              <p className="text-body-sm text-destructive" role="alert" id="grandpadsInterested-error">
                {errors.grandpadsInterested.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="how_did_you_hear">How did you hear about GrandPad? *</Label>
            <Select
              name="how_did_you_hear"
              value={howDidYouHear || ""}
              onValueChange={(value) => setValue("how_did_you_hear", value, { shouldValidate: true })}
            >
              <SelectTrigger id="how_did_you_hear" name="how_did_you_hear" aria-invalid={errors.how_did_you_hear ? "true" : "false"}
 aria-describedby={errors.how_did_you_hear ? "how_did_you_hear-error" : undefined}>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {hearAboutOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.how_did_you_hear && (
              <p className="text-body-sm text-destructive" role="alert" id="how_did_you_hear-error">
                {errors.how_did_you_hear.message}
              </p>
            )}
          </div>

          {howDidYouHear === "Other (please specify)" && (
            <div className="space-y-2">
              <Label htmlFor="how_did_you_hear_other">Please specify *</Label>
              <Input
                id="how_did_you_hear_other"
                {...register("how_did_you_hear_other")}
                placeholder="Please tell us how you heard about GrandPad"
                aria-invalid={errors.how_did_you_hear_other ? "true" : "false"}
                aria-describedby={errors.how_did_you_hear_other ? "how_did_you_hear_other-error" : undefined}
              />
              {errors.how_did_you_hear_other && (
                <p className="text-body-sm text-destructive" role="alert" id="how_did_you_hear_other-error">
                  {errors.how_did_you_hear_other.message}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="additional_message">Additional Message</Label>
            <Textarea
              id="additional_message"
              {...register("additional_message")}
              placeholder="Tell us about your specific needs or questions"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? "Submitting..." : "Book Demo"}
            </Button>
          </div>
          {submitError && (
            <p className="text-body-sm text-destructive text-center" role="alert" aria-live="polite">
              {submitError}
            </p>
          )}
        </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
